import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";

import FullModal from "@ats/src/components/modals/FullModal";
import {
  whatJobsWorkFromOptions,
  whatJobsEmploymentTypeOptions,
  whatJobsEmploymentSubTypeOptions,
  whatJobsScheduleTypeOptions,
  whatJobsStartTypeOptions,
  whatJobsSalaryDisplayTypeOptions,
  whatJobsContractPeriodOptions,
  getOptionLabel,
  deriveWorkFrom,
} from "@ats/src/lib/newLookups";
import { getFullStateName, getFullCountryName, whatJobsSectorOptions } from "@ats/src/lib/lookups";
import { titleCase } from "@ats/src/lib/utils/helpers";

type Props = {
  job: any;
  formState: any;
  whatJobsListing?: any;
  currentOrganization?: any;
  onCancel: () => void;
  isUnexpiredAndActiveOrPaused?: boolean;
};

function WhatJobsPreviewModal(props: Props) {
  const {
    job,
    formState,
    whatJobsListing,
    currentOrganization,
    onCancel,
    isUnexpiredAndActiveOrPaused,
  } = props;

  // Helper: Get value from whatJobsListing or formState (for mutable fields)
  const getValueFromListing = (key) => {
    return isUnexpiredAndActiveOrPaused && whatJobsListing?.[key] !== undefined
      ? whatJobsListing[key]
      : formState[key];
  };

  // Helper: Get value from whatJobsListing or job (for immutable fields)
  const getImmutableValue = (listingKey, jobKey) => {
    return isUnexpiredAndActiveOrPaused && whatJobsListing?.[listingKey] !== undefined
      ? whatJobsListing[listingKey]
      : job[jobKey];
  };

  const showLocationFields = isUnexpiredAndActiveOrPaused ? whatJobsListing?.whatJobsWorkFrom !== "remote" : job.remoteness !== "remoteness_only";
  // Format location for display
  const formattedCity = titleCase(job.city || "");
  const formattedState =
    job.country?.toLowerCase() === "us" && job.stateRegion
      ? getFullStateName(job.stateRegion)
      : job.stateRegion || "";

  // Use stored values from listing, no extraction
  const streetAddress = whatJobsListing?.streetAddress || "";
  const zipCode = whatJobsListing?.zipCode || "";

  // Format values using lookup options
  const formatWorkFrom = (value: string) => getOptionLabel(whatJobsWorkFromOptions, value);
  const formatEmploymentType = (value: string) =>
    getOptionLabel(whatJobsEmploymentTypeOptions, value);
  const formatEmploymentSubType = (value: string) =>
    getOptionLabel(whatJobsEmploymentSubTypeOptions, value);
  const formatScheduleType = (value: string) => getOptionLabel(whatJobsScheduleTypeOptions, value);
  const formatStartType = (value: string) => getOptionLabel(whatJobsStartTypeOptions, value);
  const formatSalaryDisplayType = (value: string) =>
    getOptionLabel(whatJobsSalaryDisplayTypeOptions, value);
  const formatContractPeriod = (value: string) =>
    getOptionLabel(whatJobsContractPeriodOptions, value);
  const formatSector = (value: string) => {
    const sectorOption = whatJobsSectorOptions.find((s: any) => s.value === value || s === value);
    return sectorOption?.label || sectorOption || value;
  };

  // Format salary unit
  const formatSalaryUnit = (unit: string): string => {
    const unitMap: { [key: string]: string } = {
      hour: "per hour",
      day: "per day",
      week: "per week",
      month: "per month",
      year: "per year",
    };
    return unitMap[unit?.toLowerCase()] || unit || "";
  };

  // Check if fields should be shown
  const showContractFields = getValueFromListing("employmentSubType") === "contract";
  const showScheduleOther = getValueFromListing("scheduleType") === "other";
  const showStartDate = getValueFromListing("startType") === "specific_date";
  const showSalaryRange =
    getValueFromListing("salaryDisplayType") === "disclose" && job.salaryMin && job.salaryMax;

  const PreviewField = ({
    label = "",
    value,
    isTitle = false,
    isImmutable = false,
    alwaysShow = false,
    shouldShow,
  }: {
    label?: string;
    value: any;
    isTitle?: boolean;
    isImmutable?: boolean;
    alwaysShow?: boolean;
    shouldShow?: boolean;
  }) => {
    if (shouldShow === false) return null;
    if (!alwaysShow && !value) return null;

    return (
      <Styled.FieldContainer>
        <Styled.Field isTitle={isTitle}>
          {label && (
            <Styled.Label>
              {label}
              {isImmutable && (
                <Styled.ImmutableLabel>
                  {isUnexpiredAndActiveOrPaused ? "(Not editable)" : "(Not editable once live)"}
                </Styled.ImmutableLabel>
              )}
            </Styled.Label>
          )}
          <Styled.Value>{alwaysShow ? value || "---" : value}</Styled.Value>
        </Styled.Field>
      </Styled.FieldContainer>
    );
  };

  const PreviewFieldFormattedHtml = ({
    label = "",
    htmlContent,
  }: {
    label?: string;
    htmlContent: string;
  }) => {
    if (!htmlContent) return null;
    return (
      <Styled.Field>
        <Styled.Label>{label}</Styled.Label>
        <Styled.FormattedTextContent dangerouslySetInnerHTML={{ __html: htmlContent }} />
      </Styled.Field>
    );
  };

  // Use stored immutable values when listing is live
  const displayJobTitle = getImmutableValue("jobTitle", "title");
  const displayCity =
    isUnexpiredAndActiveOrPaused && whatJobsListing?.city ? whatJobsListing.city : formattedCity;
  const displayState =
    isUnexpiredAndActiveOrPaused && whatJobsListing?.state ? whatJobsListing.state : formattedState;
  const displayStreetAddress = getImmutableValue("streetAddress", null) || streetAddress;
  const displayZipCode = getImmutableValue("zipCode", null) || zipCode;
  const displayCountry =
    isUnexpiredAndActiveOrPaused && whatJobsListing?.country_code
      ? getFullCountryName(whatJobsListing.country_code)
      : getFullCountryName(job.country);
  const displayWorkFrom =
    isUnexpiredAndActiveOrPaused && whatJobsListing?.whatJobsWorkFrom !== undefined
      ? formatWorkFrom(whatJobsListing.whatJobsWorkFrom)
      : deriveWorkFrom(job.remoteness);

  // Define sections with field configurations
  const sections = [
    {
      title: "Job information",
      fields: [
        {
          label: "Job Title",
          value: displayJobTitle,
          isTitle: true,
          isImmutable: true,
        },
        {
          label: "Job Sector",
          value: formatSector(getValueFromListing("sector")),
          isTitle: true,
        },
      ],
    },
    {
      title: "Location",
      fields: [
        {
          label: "Country",
          value: displayCountry,
          isImmutable: true,
          alwaysShow: true,
        },
        {
          label: "City",
          value: displayCity,
          isImmutable: true,
          shouldShow: showLocationFields || (displayCity !== "" && displayCity !== null),
        },
        {
          label: "State",
          value: displayState,
          isImmutable: true,
          shouldShow: showLocationFields || (displayState !== "" && displayState !== null),
        },
        {
          label: "Street Address",
          value: displayStreetAddress,
          isImmutable: true,
          shouldShow: showLocationFields,
        },
        {
          label: "ZIP Code",
          value: displayZipCode,
          isImmutable: true,
          alwaysShow: true,
          shouldShow: showLocationFields,
        },
      ],
    },
    {
      title: "Employment details",
      fields: [
        {
          label: "Work From",
          value: displayWorkFrom,
          isImmutable: true,
        },
        {
          label: "Employment type",
          value: formatEmploymentType(getValueFromListing("whatJobsEmploymentType")),
        },
        {
          label: "Employment sub-type",
          value: formatEmploymentSubType(getValueFromListing("employmentSubType")),
        },
        {
          label: "Contract Duration - Job Period",
          value: formatContractPeriod(getValueFromListing("contractPeriod")),
          shouldShow: showContractFields,
        },
        {
          label: "Contract Duration - Job Length",
          value: getValueFromListing("contractLength"),
          shouldShow: showContractFields,
        },
        {
          label: "Job Schedule",
          value: formatScheduleType(getValueFromListing("scheduleType")),
        },
        {
          label: "Schedule description",
          value: getValueFromListing("scheduleTypeOther"),
          shouldShow: showScheduleOther,
        },
        {
          label: "Start type",
          value: formatStartType(getValueFromListing("startType")),
        },
        {
          label: "Start by date",
          value: getValueFromListing("startByDate"),
          shouldShow: showStartDate,
        },
      ],
    },
    {
      title: "Salary",
      fields: [
        {
          label: "Salary Visibility",
          value: formatSalaryDisplayType(getValueFromListing("salaryDisplayType")),
        },
        {
          label: "Minimum",
          value: job.salaryMin ? `$${job.salaryMin}` : null,
          alwaysShow: true,
          shouldShow: showSalaryRange,
        },
        {
          label: "Maximum",
          value: job.salaryMax ? `$${job.salaryMax}` : null,
          shouldShow: showSalaryRange,
        },
        {
          label: "Rate",
          value: formatSalaryUnit(job.salaryUnit),
          shouldShow: showSalaryRange,
        },
      ],
    },
    {
      title: "Company",
      fields: [
        {
          label: "Company Name",
          value: currentOrganization?.name || "",
          alwaysShow: true,
        },
        {
          label: "Company Description",
          value: getValueFromListing("whatJobsCompanyDescription"),
          alwaysShow: true,
        },
      ],
    },
  ];

  const headerTitleText = isUnexpiredAndActiveOrPaused
    ? "WhatJobs Listing Info"
    : "WhatJobs Listing Preview";

  return (
    <FullModal onCancel={onCancel} headerTitleText={headerTitleText}>
      <Styled.Content>
        <Styled.Description>
          {isUnexpiredAndActiveOrPaused ? (
            <>
              This shows all information for your active WhatJobs listing. Fields marked as{" "}
              <strong>Not editable</strong> were set at the time of purchase and cannot be changed
              while the listing is live. All other fields can be updated.
            </>
          ) : (
            <>
              Listing information to be sent to WhatJobs on purchase. This preview shows the
              information using the labels they may use. Fields marked as{" "}
              <strong>Not editable once live</strong> cannot be changed after the listing is
              purchased.
            </>
          )}
        </Styled.Description>

        <Styled.SectionsWrapper>
          {sections.map((section) => {
            return (
              <div key={section.title}>
                <Styled.SectionTitle>{section.title}</Styled.SectionTitle>
                <Styled.Section>
                  {section.fields?.map((field, index) => (
                    <PreviewField key={field.label || index} {...field} />
                  ))}
                </Styled.Section>
              </div>
            );
          })}

          <div>
            <Styled.SectionTitle>Job Description</Styled.SectionTitle>
            <Styled.DescriptionSection>
              <PreviewFieldFormattedHtml htmlContent={job.description || ""} />
            </Styled.DescriptionSection>
          </div>
        </Styled.SectionsWrapper>
      </Styled.Content>
    </FullModal>
  );
}

export default WhatJobsPreviewModal;

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

Styled.Content = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: WhatJobsPreviewModal_Content;
    ${[t.px(4), t.pt(4), t.pb(10)]}
  `;
});

Styled.Description = styled.p((props) => {
  const t: any = props.theme;
  return css`
    label: WhatJobsPreviewModal_Description;
    ${[t.text.sm, t.mb(5)]}
    color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
    line-height: 1.5;
  `;
});

Styled.Section = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: WhatJobsPreviewModal_Section;
    ${[t.rounded.sm]}
    overflow: hidden;
    flex-shrink: 1;
  `;
});

Styled.SectionsWrapper = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: WhatJobsPreviewModal_SectionsWrapper;

    > div {
      ${[t.mt(6), t.mb(8)]}

      &:first-of-type {
        ${t.mt(0)}
      }
    }
  `;
});

Styled.SectionTitle = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: WhatJobsPreviewModal_SectionTitle;
    ${[t.text.h4, t.mb(2)]}
    color: ${t.dark ? t.color.gray[200] : t.color.black};
    display: flex;
    align-items: center;
  `;
});

Styled.FieldContainer = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: WhatJobsPreviewModal_FieldContainer;
    border-bottom: 1px solid ${t.dark ? "transparent" : t.color.gray[300]};
    border-left: 1px solid ${t.dark ? "transparent" : t.color.gray[300]};
    border-right: 1px solid ${t.dark ? "transparent" : t.color.gray[300]};
    background-color: ${t.dark ? "rgba(255,255,255,0.07)" : t.color.white};

    &:first-of-type {
      border-top: 1px solid ${t.dark ? "transparent" : t.color.gray[300]};
      border-top-left-radius: 0.3125rem;
      border-top-right-radius: 0.3125rem;
    }

    &:last-of-type {
      border-bottom: 1px solid ${t.dark ? "transparent" : t.color.gray[300]};
      border-bottom-left-radius: 0.3125rem;
      border-bottom-right-radius: 0.3125rem;
    }
  `;
});

Styled.Field = styled.div<{ isTitle?: boolean }>((props) => {
  const t: any = props.theme;
  return css`
    label: WhatJobsPreviewModal_Field;
    ${[t.px(2), t.py(2)]}
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    width: 100%;
    min-height: ${t.spacing[10]};

    ${t.mq["md"]} {
      min-height: ${t.spacing[8]};
    }
  `;
});

Styled.Label = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: WhatJobsPreviewModal_Label;
    ${[t.text.sm, t.text.bold]}
    color: ${t.dark ? t.color.gray[300] : t.color.black};
    display: inline-block;
    line-height: 1.4;

    ${t.mq["md"]} {
      ${[t.text.xs]}
    }
  `;
});

Styled.Value = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: WhatJobsPreviewModal_Value;
    ${[t.text.base]}
    color: ${t.dark ? t.color.gray[200] : t.color.gray[700]};
    white-space: pre-wrap;
    word-wrap: break-word;
    line-height: 1.4;

    ${t.mq["md"]} {
      ${[t.text.sm]}
    }
  `;
});

Styled.ImmutableLabel = styled.span((props) => {
  const t: any = props.theme;
  return css`
    label: WhatJobsPreviewModal_ImmutableLabel;
    ${[t.text.xs, t.ml(1)]}
    color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
    font-weight: normal;
  `;
});

Styled.DescriptionSection = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: WhatJobsPreviewModal_DescriptionSection;
    ${[t.px(2), t.mt(3), t.rounded.sm]}
    border: 1px solid ${t.dark ? "transparent" : t.color.gray[300]};
    background-color: ${t.dark ? "rgba(255,255,255,0.07)" : t.color.white};
  `;
});

Styled.FormattedTextContent = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: WhatJobsPreviewModal_FormattedTextContent;
    font-size: 14px;
    line-height: 1.6;
    font-weight: 400;
    color: ${t.dark ? t.color.gray[200] : t.color.black};

    > *:last-child {
      margin-bottom: 0;
    }

    h1 {
      font-size: 20px;
      font-weight: 600;
      margin-top: 32px;
      margin-bottom: 8px;
      line-height: 1.6;
      color: ${t.dark ? t.color.gray[100] : t.color.black};

      &:first-child {
        margin-top: 0;
      }
    }

    h2 {
      font-size: 18px;
      font-weight: 600;
      margin-top: 24px;
      margin-bottom: 8px;
      line-height: 1.4;
      color: ${t.dark ? t.color.gray[100] : t.color.black};

      &:first-child {
        margin-top: 0;
      }
    }

    h3 {
      font-size: 16px;
      font-weight: 600;
      margin-top: 16px;
      margin-bottom: 4px;
      line-height: 1.4;
      color: ${t.dark ? t.color.gray[100] : t.color.black};

      &:first-child {
        margin-top: 0;
      }
    }

    h4 {
      font-size: 14px;
      font-weight: 600;
      margin-top: 16px;
      margin-bottom: 4px;
      line-height: 1.5;
      color: ${t.dark ? t.color.gray[100] : t.color.black};

      &:first-child {
        margin-top: 0;
      }
    }

    h5 {
      font-size: 12px;
      font-weight: 600;
      margin-top: 16px;
      margin-bottom: 4px;
      line-height: 1.6;
      color: ${t.dark ? t.color.gray[100] : t.color.black};

      &:first-child {
        margin-top: 0;
      }
    }

    h6 {
      font-size: 12px;
      font-weight: 600;
      margin-top: 16px;
      margin-bottom: 4px;
      line-height: 1.6;
      color: ${t.dark ? t.color.gray[100] : t.color.black};

      &:first-child {
        margin-top: 0;
      }
    }

    p {
      line-height: 1.6;
      margin-bottom: 18px;
      color: ${t.dark ? t.color.gray[200] : t.color.black};
    }

    b,
    strong {
      font-weight: 600;
    }

    a,
    a:link,
    a:visited {
      text-decoration: underline;
      font-weight: 450;
      color: ${t.dark ? t.color.gray[300] : t.color.gray[700]};
    }

    blockquote {
      color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
      margin-bottom: 12px;
      padding-left: 12px;
      border-left: 4px solid ${t.dark ? t.color.gray[700] : t.color.gray[200]};
      line-height: 1.6;
    }

    ul {
      margin-bottom: 12px;
      padding-left: 24px;
      list-style-type: disc;
      line-height: 1.6;

      p {
        margin-bottom: 0px;
      }
    }

    ol {
      margin-bottom: 12px;
      padding-left: 24px;
      list-style-type: decimal;
      line-height: 1.6;

      p {
        margin-bottom: 0px;
      }
    }

    ul ul,
    ol ul {
      margin-top: 6px;
      margin-bottom: 4px;
      padding-left: 18px;
      list-style-type: circle;
    }

    ol ol,
    ul ol {
      margin-top: 6px;
      margin-bottom: 4px;
      padding-left: 18px;
      list-style-type: lower-latin;
    }

    li {
      margin-bottom: 6px;
    }

    img {
      margin: 16px 0;
      max-width: 100%;
      border-radius: 7px;
      overflow: hidden;

      &:first-child {
        margin-top: 0;
      }
    }

    code {
      padding: 20px 24px;
      background-color: ${t.dark ? t.color.gray[800] : t.color.gray[100]};
      border-radius: 7px;
      overflow-x: scroll;
      white-space: pre-wrap;
    }

    .wrk-iframe-container {
      display: block;
      position: relative;
      padding-top: 56.25%;
      margin: 16px 0;
      max-width: 100%;
      border-radius: 7px;
      overflow: hidden;

      &:first-child {
        margin-top: 0;
      }

      iframe {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
      }
    }

    hr {
      border: none;
      height: 1px;
      background-color: ${t.dark ? t.color.gray[600] : t.color.gray[400]};
    }
  `;
});
